import { BarChart3, Info, Lightbulb, Trophy, Users } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useId, useMemo, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";

import { SectionCard } from "@/components/competitors/ui";
import {
  EmptyState,
  LoadingCards,
  PillGroup,
} from "@/components/market-intel/components";
import { SELECT_CLASS } from "@/components/market-intel/FormDialog";
import { toIsoDay } from "@/components/market-intel/dates";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMoneyFormat } from "@/hooks/use-money-format";
import { formatDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

import {
  ANALYTICS_PERIODS,
  buildInsights,
  filterMetrics,
  indexById,
  interactionsOf,
  isAnalyticsPeriod,
  periodStartWeek,
  pieceStats,
  statsByChannel,
  statsByFormat,
  sumMetrics,
  topPieces,
  weeklyKeySeries,
  engagementRate,
  type PieceStats,
  type WeekPoint,
} from "./analytics";
import { parseDay } from "./calendar";
import { formatLabelKey } from "./constants";
import { useMarketing } from "./context";
import { findById } from "./hooks";
import { BarList, ChannelLabel, FormatChip, KpiTile } from "./ui";

const KEY_SERIES = [
  { key: "saves", color: "var(--mk-series-1)" },
  { key: "shares", color: "var(--mk-series-2)" },
  { key: "direct_messages", color: "var(--mk-series-3)" },
] as const;

/**
 * Marketing > Analíticas: totals of the weekly rows in the period, the
 * three key numbers week by week, best pieces, channels and formats, and a
 * few automatic conclusions.
 */
export const AnalyticsView = ({ isPending }: { isPending: boolean }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { formatMoney } = useMoneyFormat();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { data, recordMetrics } = useMarketing();
  const periodParam = params.get("periodo");
  const period = isAnalyticsPeriod(periodParam) ? periodParam : "90";
  const channel = params.get("canal") ?? "";
  const [showHelp, setShowHelp] = useState(false);
  const helpId = useId();

  const setParam = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const computed = useMemo(() => {
    const itemsById = indexById(data.items);
    const rows = filterMetrics(data.metrics, itemsById, {
      startWeek: periodStartWeek(period, toIsoDay(new Date())),
      channel,
    });
    const totals = sumMetrics(rows);
    const stats = pieceStats(rows, itemsById);
    const byChannel = statsByChannel(rows, itemsById);
    const byFormat = statsByFormat(rows, itemsById);
    const series = weeklyKeySeries(rows);
    return { rows, totals, stats, byChannel, byFormat, series };
  }, [data.items, data.metrics, period, channel]);

  const insights = useMemo(
    () =>
      buildInsights({
        ...computed,
        channels: data.channels,
        formatLabel: (format) =>
          translate(formatLabelKey(format)).toLowerCase(),
        weekdayLabel: (isoDay) =>
          formatDate(new Date(2026, 10, 1 + isoDay), locale, {
            weekday: "long",
          }),
      }),
    [computed, data.channels, translate, locale],
  );

  if (isPending) return <LoadingCards />;

  const { rows, totals, stats, byChannel, byFormat, series } = computed;
  const rate = engagementRate(totals);
  const percent = (value: number | null) =>
    value == null
      ? "—"
      : `${(value * 100).toLocaleString(locale, { maximumFractionDigits: 1 })} %`;
  const number = (value: number) => value.toLocaleString(locale);

  return (
    <section className="space-y-4">
      <h2 className="sr-only">{translate("marketing.analytics.heading")}</h2>
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <PillGroup
          label={translate("marketing.analytics.period_label")}
          value={period}
          options={ANALYTICS_PERIODS.map((value) => ({
            value,
            label: translate(`marketing.analytics.period_${value}`),
          }))}
          onChange={(next) => setParam("periodo", next === "90" ? null : next)}
        />
        <div className="flex items-center gap-2 md:ml-auto">
          <label htmlFor="mk-an-channel" className="sr-only">
            {translate("marketing.common.filter_channel")}
          </label>
          <select
            id="mk-an-channel"
            className={cn(
              SELECT_CLASS,
              "h-9 min-w-0 flex-1 rounded-full md:w-52",
            )}
            value={channel}
            onChange={(event) => setParam("canal", event.target.value || null)}
          >
            <option value="">
              {translate(
                isMobile
                  ? "marketing.common.channels_short"
                  : "marketing.common.all_channels",
              )}
            </option>
            {data.channels.map((entry) => (
              <option key={entry.id} value={String(entry.id)}>
                {entry.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            className="btn-save h-9 shrink-0 rounded-full px-4"
            onClick={() => recordMetrics()}
          >
            <BarChart3 aria-hidden className="size-4" />
            {translate("marketing.analytics.record")}
          </Button>
        </div>
      </div>

      {!rows.length ? (
        <EmptyState
          icon={BarChart3}
          title={translate("marketing.analytics.empty")}
          hint={translate("marketing.analytics.empty_hint")}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4">
            <KpiTile
              index={0}
              label={translate("marketing.metric.reach")}
              value={number(totals.reach)}
            />
            <KpiTile
              index={1}
              label={translate("marketing.analytics.interactions")}
              value={number(interactionsOf(totals))}
            />
            <div
              className="mi-card mi-rise flex min-w-0 flex-col gap-1 p-3.5 md:p-4"
              style={{ "--mi-index": 2 } as CSSProperties}
            >
              <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                {translate("marketing.analytics.engagement")}
                <button
                  type="button"
                  className="mk-info"
                  aria-expanded={showHelp}
                  aria-controls={helpId}
                  aria-label={translate("marketing.analytics.engagement_help")}
                  title={translate("marketing.analytics.engagement_help")}
                  onClick={() => setShowHelp((open) => !open)}
                >
                  <Info aria-hidden className="size-3.5" />
                </button>
              </span>
              <span className="text-2xl leading-none font-semibold tracking-tight tabular-nums">
                {percent(rate)}
              </span>
              <span
                id={helpId}
                role="note"
                hidden={!showHelp}
                className="mk-help"
              >
                {translate("marketing.analytics.engagement_help")}
              </span>
            </div>
            <KpiTile
              index={3}
              label={translate("marketing.metric.leads")}
              value={number(totals.leads)}
            />
            <KpiTile
              index={4}
              label={translate("marketing.metric.saves")}
              value={number(totals.saves)}
              tone="brand"
            />
            <KpiTile
              index={5}
              label={translate("marketing.metric.shares")}
              value={number(totals.shares)}
              tone="brand"
            />
            <KpiTile
              index={6}
              label={translate("marketing.metric.direct_messages")}
              value={number(totals.direct_messages)}
              tone="brand"
            />
            <KpiTile
              index={7}
              label={translate("marketing.analytics.spend")}
              value={formatMoney(totals.spend)}
            />
          </div>

          <SectionCard
            id="mk-weekly"
            icon={BarChart3}
            title={translate("marketing.analytics.key_numbers")}
          >
            <p className="mb-3 text-sm text-muted-foreground">
              {translate("marketing.analytics.key_numbers_hint")}
            </p>
            <WeeklyChart series={series} />
          </SectionCard>

          <SectionCard
            id="mk-insights"
            icon={Lightbulb}
            title={translate("marketing.analytics.insights")}
          >
            {insights.length ? (
              <ul className="flex flex-col gap-2">
                {insights.map((insight) => (
                  <li key={insight.id} className="mk-insight">
                    {translate(
                      insight.key,
                      Object.fromEntries(
                        Object.entries(insight.args).map(([key, value]) => [
                          key,
                          typeof value === "number"
                            ? value.toLocaleString(locale)
                            : value,
                        ]),
                      ),
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {translate("marketing.analytics.insights_empty")}
              </p>
            )}
          </SectionCard>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TopCard
              id="mk-top-engagement"
              title={translate("marketing.analytics.top_engagement")}
              stats={topPieces(stats, "engagement")}
              value={(stat) => stat.interactions}
              icon={Trophy}
            />
            <TopCard
              id="mk-top-leads"
              title={translate("marketing.analytics.top_leads")}
              stats={topPieces(stats, "leads")}
              value={(stat) => stat.totals.leads}
              icon={Users}
            />
            <SectionCard
              id="mk-by-channel"
              icon={BarChart3}
              title={translate("marketing.analytics.by_channel")}
            >
              <BarList
                label={translate("marketing.analytics.by_channel")}
                data={byChannel.map((group) => {
                  const entry = findById(data.channels, group.key);
                  return {
                    key: group.key,
                    label: <ChannelLabel channel={entry} />,
                    value: group.interactions,
                    display: `${number(group.interactions)} · ${percent(group.rate)}`,
                  };
                })}
              />
            </SectionCard>
            <SectionCard
              id="mk-by-format"
              icon={BarChart3}
              title={translate("marketing.analytics.by_format")}
            >
              <BarList
                label={translate("marketing.analytics.by_format")}
                data={byFormat.map((group) => ({
                  key: group.key,
                  label: (
                    <span className="inline-flex items-center gap-2">
                      <FormatChip format={group.key} />
                      <span className="text-xs text-muted-foreground">
                        {translate("marketing.analytics.pieces_count", {
                          smart_count: group.pieces,
                        })}
                      </span>
                    </span>
                  ),
                  value: group.interactions,
                  display: `${number(group.interactions)} · ${percent(group.rate)}`,
                  color: "var(--mk-series-2)",
                }))}
              />
            </SectionCard>
          </div>
        </>
      )}
    </section>
  );
};

const TopCard = ({
  id,
  title,
  stats,
  value,
  icon,
}: {
  id: string;
  title: string;
  stats: PieceStats[];
  value: (stat: PieceStats) => number;
  icon: typeof Trophy;
}) => {
  const translate = useTranslate();
  const { data, openPiece } = useMarketing();
  return (
    <SectionCard id={id} icon={icon} title={title}>
      {stats.length ? (
        <ol className="flex flex-col">
          {stats.map((stat, index) => (
            <li key={stat.item.id}>
              <button
                type="button"
                className="mk-row group"
                onClick={() => openPiece(stat.item.id)}
              >
                <span className="mk-rank" aria-hidden>
                  {index + 1}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-1 text-left">
                  <span className="text-sm leading-snug font-medium [overflow-wrap:anywhere] group-hover:text-primary">
                    {stat.item.title}
                  </span>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <ChannelLabel
                      channel={findById(data.channels, stat.item.channel_id)}
                    />
                    <FormatChip format={stat.item.format} />
                  </span>
                </span>
                <span className="shrink-0 text-lg font-semibold tabular-nums">
                  {value(stat)}
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("marketing.analytics.top_empty")}
        </p>
      )}
    </SectionCard>
  );
};

/** Grouped columns per week (CSS), with a data table for screen readers. */
const WeeklyChart = ({ series }: { series: WeekPoint[] }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const max = Math.max(
    1,
    ...series.flatMap((point) => KEY_SERIES.map(({ key }) => point[key])),
  );
  const label = (week: string) =>
    formatDate(parseDay(week), locale, { day: "numeric", month: "short" });
  return (
    <figure className="m-0">
      <figcaption className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium">
        {KEY_SERIES.map(({ key, color }) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 rounded-sm"
              style={{ background: color }}
            />
            {translate(`marketing.metric.${key}`)}
          </span>
        ))}
      </figcaption>
      <div className="mk-weekly" aria-hidden>
        {series.map((point) => (
          <div key={point.week} className="mk-weekly__group">
            <div className="mk-weekly__bars">
              {KEY_SERIES.map(({ key, color }) => (
                <span
                  key={key}
                  className="mk-weekly__bar"
                  title={`${translate(`marketing.metric.${key}`)}: ${point[key]}`}
                  style={
                    {
                      "--mk-h": `${(point[key] / max) * 100}%`,
                      background: color,
                    } as CSSProperties
                  }
                />
              ))}
            </div>
            <span className="mk-weekly__label">{label(point.week)}</span>
          </div>
        ))}
      </div>
      <table className="sr-only">
        <caption>{translate("marketing.analytics.weekly")}</caption>
        <thead>
          <tr>
            <th scope="col">{translate("marketing.metrics_form.week")}</th>
            {KEY_SERIES.map(({ key }) => (
              <th key={key} scope="col">
                {translate(`marketing.metric.${key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map((point) => (
            <tr key={point.week}>
              <th scope="row">
                {translate("marketing.analytics.week_of", {
                  day: label(point.week),
                })}
              </th>
              {KEY_SERIES.map(({ key }) => (
                <td key={key}>{point[key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
};
